// @system — Per-page copy/content resolver (@custom → @system fallback).
//
// Products override page copy by editing content/@custom/<page>.js (never the
// @system defaults, which are overwritten during template sync). The resolver is
// SYNCHRONOUS: both module sets are statically imported below, so a page's very
// first paint already shows the merged copy — no async flash, no empty object.
//
//   import { useContent } from '@/app/hooks/@system/useContent'
//   import { copyText } from '@/app/lib/@system/content'
//   const content = useContent('landing')
//   const title = copyText(content, 'heroTitle', info.tagline)
//
// Precedence used by LandingPage / AuthPage / PricingPage per field:
//   config text (@/config text.<page>.*)  →  content/@custom  →  content/@system  →  literal
//
// Registering a new page: add content/@system/<page>.js (non-empty strings),
// content/@custom/<page>.js (export default {}) and an entry in REGISTRY.
import landingSystem from '@/app/content/@system/landing'
import authSystem from '@/app/content/@system/auth'
import pricingSystem from '@/app/content/@system/pricing'

import landingCustom from '@/app/content/@custom/landing'
import authCustom from '@/app/content/@custom/auth'
import pricingCustom from '@/app/content/@custom/pricing'

/**
 * A page's copy map. @system modules export non-empty strings; @custom
 * overrides are hand-edited, so values are `unknown` until copyText() checks them.
 * @typedef {Record<string, unknown>} ContentMap
 */

/** @typedef {'auth' | 'landing' | 'pricing'} ContentPageKey */

/** @typedef {{ system: ContentMap, custom: ContentMap }} ContentRegistryEntry */

/** @type {Record<ContentPageKey, ContentRegistryEntry>} */
const REGISTRY = {
  auth: { system: authSystem, custom: authCustom },
  landing: { system: landingSystem, custom: landingCustom },
  pricing: { system: pricingSystem, custom: pricingCustom },
}

/** Page keys that take part in the @custom → @system chain. */
export const CONTENT_PAGE_KEYS = Object.freeze(/** @type {ContentPageKey[]} */ (Object.keys(REGISTRY)))

/**
 * @param {unknown} v
 * @returns {v is Record<string, unknown>}
 */
const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v)

/**
 * True when `key` is a registered content page.
 * @param {unknown} key
 * @returns {key is ContentPageKey}
 */
export function isContentPage(key) {
  return typeof key === 'string' && Object.prototype.hasOwnProperty.call(REGISTRY, key)
}

/**
 * Merge @custom over @system. @custom wins on any shared key; @system keys the
 * product did not touch and @custom-only keys are both preserved. `undefined`
 * values in @custom are ignored (they cannot blank a default). Blank strings
 * are kept here and rejected by copyText(), which is the last seam before render.
 * @param {unknown} system
 * @param {unknown} custom
 * @returns {ContentMap}
 */
export function mergeContent(system, custom) {
  /** @type {ContentMap} */
  const merged = isPlainObject(system) ? { ...system } : {}
  if (isPlainObject(custom)) {
    for (const [key, value] of Object.entries(custom)) {
      if (value !== undefined) merged[key] = value
    }
  }
  return merged
}

/**
 * Fresh copy of the @system default module for `key` ({} for unknown keys).
 * @param {unknown} key
 * @returns {ContentMap}
 */
export function systemDefaultFor(key) {
  return isContentPage(key) ? { ...REGISTRY[key].system } : {}
}

/**
 * Merged @custom-over-@system copy for `key` ({} for unknown keys).
 * @param {unknown} key
 * @returns {ContentMap}
 */
export function resolveContent(key) {
  if (!isContentPage(key)) return {}
  const { system, custom } = REGISTRY[key]
  return mergeContent(system, custom)
}

/**
 * Read one string from resolved content with a literal fallback. Only a
 * non-blank string counts; whitespace-only, missing, null or non-string values
 * fall back so a hand-edited override can never blank a heading. Leading
 * whitespace is dropped, the rest of the value is returned untouched.
 * @template F
 * @param {ContentMap | null | undefined} content
 * @param {string} key
 * @param {F} fallback
 * @returns {string | F}
 */
export function copyText(content, key, fallback) {
  const value = content?.[key]
  if (typeof value === 'string' && value.trim().length > 0) return value.trimStart()
  return fallback
}

// ─── Backwards-compatible API ────────────────────────────────────────────────
// Older pages used an async loader + cache. Resolution is now synchronous, so
// these are thin wrappers kept so existing @custom code keeps compiling.

/**
 * @deprecated use resolveContent(key) — kept for @custom callers.
 * @param {unknown} key
 * @returns {Promise<ContentMap>}
 */
export async function loadContent(key) {
  return resolveContent(key)
}

/**
 * @deprecated use resolveContent(key) — kept for @custom callers.
 * @param {unknown} key
 * @returns {ContentMap}
 */
export function getContent(key) {
  return resolveContent(key)
}

/** @deprecated no cache exists anymore — no-op kept for @custom callers. */
export function clearContentCache() {}
